/**
 * Turn a backend `role_forbidden` 403 into copy an operator can act on.
 *
 * ENG-ROLE-GATE-FOLLOWTHROUGH Item 2.
 *
 * WHY THIS EXISTS. `backend/app/auth/dependencies.py:136-143` raises
 * `{"error": "role_forbidden", "required_one_of": [...], "actual_role": "..."}`
 * — a structured detail with **no `message` key**. `services/api.ts`'s
 * `extractMessage()` prefers `message` → `error_message` → `detail` → and only
 * then falls back to `record.error`, so for this one shape the fallback wins and
 * `ApiError.message` becomes the literal string `"role_forbidden"`. Every
 * surface that renders `err.message` therefore shows the operator a machine
 * code. Fixing it inside `extractMessage` was rejected: that fallback is the
 * last-resort path for EVERY error code, and changing it would alter the
 * message of unrelated failures that legitimately have no human sentence.
 *
 * The payload carries exactly what the message needs — which roles would have
 * worked, and which one the operator actually holds — so the copy can say
 * something true and specific instead of "something went wrong".
 *
 * Deliberately React-free and i18n-free: it returns a KEY plus params for the
 * caller's `t()`, so it is unit-testable without mounting a component or
 * booting i18next, and so the copy stays in the catalogs where the locale
 * parity gate can see it.
 */

import type { TFunction } from "i18next";

const ROLE_FORBIDDEN_CODE = "role_forbidden";

/** i18n key for the operator-facing sentence. Lives under the shared
 *  `common.*` namespace — created by this change; see the catalogs. */
export const ROLE_FORBIDDEN_MESSAGE_KEY = "common.roleForbidden.message";

export interface RoleForbiddenCopy {
  key: typeof ROLE_FORBIDDEN_MESSAGE_KEY;
  params: { roles: string; actual: string };
}

interface RoleForbiddenDetail {
  requiredOneOf: string[];
  actualRole: string | null;
}

function titleCase(role: string): string {
  return role.length ? role[0].toUpperCase() + role.slice(1) : role;
}

/** Read the structured 403 body, or null when this is not a role refusal. */
export function roleForbiddenDetail(
  error: unknown,
): RoleForbiddenDetail | null {
  if (!error || typeof error !== "object") return null;

  // `ApiError` exposes the parsed code plus the raw body; both are checked
  // because a caller may hand us the body directly (some hooks unwrap it).
  const candidate = error as {
    code?: unknown;
    body?: unknown;
    detail?: unknown;
    error?: unknown;
  };

  const bodies: unknown[] = [candidate.body, candidate.detail, candidate];
  for (const body of bodies) {
    if (!body || typeof body !== "object") continue;
    const record = body as Record<string, unknown>;
    // The detail can be nested one level (`{detail: {...}}`) — axios bodies
    // arrive both ways depending on whether FastAPI wrapped the payload.
    const inner =
      record.detail && typeof record.detail === "object"
        ? (record.detail as Record<string, unknown>)
        : record;
    const code =
      typeof inner.error === "string"
        ? inner.error
        : typeof candidate.code === "string"
          ? candidate.code
          : null;
    if (code !== ROLE_FORBIDDEN_CODE) continue;
    const required = Array.isArray(inner.required_one_of)
      ? inner.required_one_of.filter((r): r is string => typeof r === "string")
      : [];
    const actual =
      typeof inner.actual_role === "string" ? inner.actual_role : null;
    return { requiredOneOf: required, actualRole: actual };
  }
  return null;
}

/** True when the error is the backend's role refusal. */
export function isRoleForbidden(error: unknown): boolean {
  return roleForbiddenDetail(error) !== null;
}

/**
 * The i18n key + params for a role refusal, or null to let the caller keep its
 * existing error handling. Returning null rather than a generic fallback is
 * deliberate: this helper must never swallow an unrelated failure into a
 * misleading "you lack permission" message.
 */
export function roleForbiddenCopy(error: unknown): RoleForbiddenCopy | null {
  const detail = roleForbiddenDetail(error);
  if (detail === null) return null;
  return {
    key: ROLE_FORBIDDEN_MESSAGE_KEY,
    params: {
      // Comma-joined rather than "X or Y": the conjunction differs per locale
      // and building it in code would force each catalog to inherit English
      // grammar. A list reads correctly in all three.
      roles: detail.requiredOneOf.map(titleCase).join(", "),
      actual: detail.actualRole ? titleCase(detail.actualRole) : "",
    },
  };
}

/**
 * Drop-in replacement for `err.message` at any surface that renders a mutation
 * failure.
 *
 * Returns the role-specific sentence for a `role_forbidden` 403 and the
 * error's own message for everything else, so a call site swaps ONE token and
 * keeps its existing copy, fallbacks and layout. That matters: there are ~18
 * such controls, and the alternative — each one hand-rolling its own
 * `if (code === "role_forbidden")` branch — is precisely how two spellings of
 * the same check drift apart with nothing going red.
 */
export function resolveErrorMessage(t: TFunction, error: unknown): string {
  const copy = roleForbiddenCopy(error);
  if (copy !== null) return t(copy.key, copy.params);
  const tierCopy = tierNotEligibleCopy(error);
  if (tierCopy !== null) return t(tierCopy.key, tierCopy.params);
  const message = (error as { message?: unknown } | null)?.message;
  return typeof message === "string" ? message : "";
}

/**
 * The published-packaging refusal (PRICING.md § Starter — CPP / CSL management
 * and Programs are Pro and up; PO 2026-09-20): the backend answers 403
 * `{"error": "tier_not_eligible", "feature": "programs" | "targeting_pages",
 * "upgrade_target": "pro", ...}` (`app.billing.packaging_gate`). Rendered in
 * words with the upgrade pointer, never as the raw slug.
 */
export const TIER_NOT_ELIGIBLE_MESSAGE_KEY = "common.tierNotEligible.message";

const TIER_FEATURE_KEYS: Record<string, string> = {
  programs: "common.tierNotEligible.features.programs",
  targeting_pages: "common.tierNotEligible.features.targetingPages",
};

export function tierNotEligibleCopy(error: unknown): {
  key: typeof TIER_NOT_ELIGIBLE_MESSAGE_KEY;
  params: { feature: string };
} | null {
  const code = (error as { code?: unknown } | null)?.code;
  if (code !== "tier_not_eligible") return null;
  const detail = (error as { detail?: unknown } | null)?.detail;
  const feature =
    detail &&
    typeof detail === "object" &&
    typeof (detail as { feature?: unknown }).feature === "string"
      ? String((detail as { feature: string }).feature)
      : "";
  return {
    key: TIER_NOT_ELIGIBLE_MESSAGE_KEY,
    params: {
      feature:
        TIER_FEATURE_KEYS[feature] ?? "common.tierNotEligible.features.generic",
    },
  };
}

/**
 * Billing / Stripe failures on the two card-entry surfaces (the Step-1 plan
 * page and the exit CC-trial gate), ENG-NEW-USER-FUNNEL-PROOF wave 2 T3.
 *
 * WHY THIS EXISTS. `app/api/v1/billing.py` answers with structured details
 * (`stripe_api_error`, `price_not_configured`, `signup_state_conflict`,
 * `not_authenticated`) and `services/api.ts`'s `extractMessage()` hands the
 * caller either the bare machine slug (no `message` key) or a developer
 * sentence ("Workspace signup_state='trial_active' is already a terminal
 * state; ... docs/product/technical-architecture.md ..."). A transport failure
 * (`status === null`) surfaces axios' "Network Error" and a 5xx its status
 * text. None of those belong in front of an operator mid-checkout, so every
 * card-surface catch site maps them to catalog copy through this helper.
 *
 * Returns a KEY, never a sentence, for the same reason `roleForbiddenCopy`
 * does: the copy lives in the catalogs where the parity gate can see it. The
 * `price_not_configured` sentence is the surface's own existing
 * "billing not configured" string, so the caller passes its key in.
 */
export const BILLING_ERROR_KEYS = {
  cardServiceUnavailable: "billing.errors.cardServiceUnavailable",
  signupStateConflict: "billing.errors.signupStateConflict",
  notAuthenticated: "billing.errors.notAuthenticated",
  network: "billing.errors.network",
  server: "billing.errors.server",
} as const;

export type BillingNotConfiguredKey =
  | "pages.onboardingPlan.billingNotConfiguredMessage"
  | "onboarding.ccTrialGate.billingNotConfiguredMessage";

export type BillingErrorKey =
  | (typeof BILLING_ERROR_KEYS)[keyof typeof BILLING_ERROR_KEYS]
  | BillingNotConfiguredKey;

export function billingErrorCopy(
  error: unknown,
  notConfiguredKey: BillingNotConfiguredKey,
): { key: BillingErrorKey } | null {
  if (!error || typeof error !== "object") return null;
  const { code, status } = error as { code?: unknown; status?: unknown };
  if (code === "stripe_api_error") {
    return { key: BILLING_ERROR_KEYS.cardServiceUnavailable };
  }
  if (code === "price_not_configured") return { key: notConfiguredKey };
  if (code === "signup_state_conflict" || status === 409) {
    return { key: BILLING_ERROR_KEYS.signupStateConflict };
  }
  if (code === "not_authenticated" || status === 401) {
    return { key: BILLING_ERROR_KEYS.notAuthenticated };
  }
  // `ApiError.status` is `null` (not undefined) when no response arrived.
  if (status === null) return { key: BILLING_ERROR_KEYS.network };
  if (typeof status === "number" && status >= 500) {
    return { key: BILLING_ERROR_KEYS.server };
  }
  return null;
}

/**
 * Drop-in for {@link resolveErrorMessage} at the billing catch sites: the
 * mapped catalog sentence when {@link billingErrorCopy} recognises the
 * failure, otherwise the role / tier / error-message resolution unchanged.
 */
export function resolveBillingErrorMessage(
  t: TFunction,
  error: unknown,
  notConfiguredKey: BillingNotConfiguredKey,
): string {
  const copy = billingErrorCopy(error, notConfiguredKey);
  return copy !== null ? t(copy.key) : resolveErrorMessage(t, error);
}

/** i18n key for the PREEMPTIVE hint shown in place of a control the current
 *  role cannot use (as opposed to the message shown after a refusal). */
export const ROLE_RESTRICTED_HINT_KEY = "common.roleRestricted.hint";

// NOTE: the capability predicates (`roleOf`, `canManageBilling`, `canDeleteApp`)
// live in `@/lib/roles`. This module owns only the MESSAGE half, so there is
// exactly one home for each concern.

/** Params for {@link ROLE_RESTRICTED_HINT_KEY}, formatted like the refusal copy. */
export function roleRestrictedParams(allowed: readonly string[]): {
  roles: string;
} {
  return { roles: allowed.map(titleCase).join(", ") };
}
